import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import { Button, LoadingState, ErrorState, EmptyState, Avatar } from '../components/ui';
import PremiumName from '../components/ui/PremiumName';
import { MasonryGrid, PostCard } from '../components/posts';
import { ServiceCard, ServiceModal } from '../components/services';
import { PostSuggestion, ServiceSuggestion } from '../components/search';
import { useAuth } from '../hooks/useAuth';
import { usePosts } from '../hooks/usePosts';
import { useSearch } from '../hooks/useSearch';
import { normalizeService, searchPosts, allWordsPresent } from '../services/postService';
import { CATEGORIES } from '../constants/categories';
import { db } from '../firebase';
import { collection, getDocs, limit, orderBy, query } from 'firebase/firestore';
import { getHeroImageUrls } from '../services/postService';
import { getHeroPortfolioImageUrls } from '../services/portfolioService';
import Hero from '../components/home/Hero';

const ExplorePage = () => {
  const { userData } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const q = (searchParams.get('q') || '').trim();

  const [inputValue, setInputValue] = useState(q);
  const inputValueRef = useRef(inputValue);
  inputValueRef.current = inputValue;

  const [selectedCategory, setSelectedCategory] = useState('all');
  const [selectedService, setSelectedService] = useState(null);

  const [heroImageUrls, setHeroImageUrls] = useState([]);

  // Feed base (cuando no hay query)
  const { posts: explorePosts, loading: exploreLoading, error: exploreError, hasMore, loadMore, refresh } =
    usePosts('explore', userData?.id);

  // Búsqueda unificada (cuando hay query)
  const { users, posts: searchedPosts, services, loading: searchLoading, error: searchError } = useSearch(q);

  useEffect(() => {
    setInputValue(q);
  }, [q]);

  const blockedIds = useMemo(() => (Array.isArray(userData?.blocked) ? userData.blocked : []), [userData?.blocked]);

  // Hero: igual que Home (portfolio -> fallback posts)
  useEffect(() => {
    let cancelled = false;
    getHeroPortfolioImageUrls(15).then((urls) => {
      if (cancelled) return;
      if (Array.isArray(urls) && urls.length >= 3) {
        setHeroImageUrls(urls);
      } else {
        getHeroImageUrls(15).then((fallback) => {
          if (!cancelled && Array.isArray(fallback)) setHeroImageUrls(fallback);
        });
      }
    });
    return () => { cancelled = true; };
  }, []);

  // Sugerencias dentro de la página (como SearchPage)
  const [suggestions, setSuggestions] = useState({ users: [], posts: [], services: [] });
  const [showSuggestions, setShowSuggestions] = useState(false);
  const searchContainerRef = useRef(null);

  const fetchSuggestions = useCallback(async (searchTerm) => {
    const term = (searchTerm || '').trim();
    if (!term) {
      setSuggestions({ users: [], posts: [], services: [] });
      setShowSuggestions(false);
      return;
    }
    try {
      const firstWord = term.toLowerCase().split(/\s+/)[0] || '';
      const usersRef = collection(db, 'users');
      const usersSnapshot = await getDocs(query(usersRef, limit(80)));
      const foundUsers = usersSnapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((u) => {
          const username = u.username || '';
          const displayName = u.displayName || '';
          const email = u.email || '';
          const combined = `${username} ${displayName} ${email}`;
          return allWordsPresent(combined, term);
        })
        .sort((a, b) => {
          const aName = (a.username || a.displayName || '').toLowerCase();
          const bName = (b.username || b.displayName || '').toLowerCase();
          const aStarts = aName.startsWith(firstWord);
          const bStarts = bName.startsWith(firstWord);
          if (aStarts && !bStarts) return -1;
          if (!aStarts && bStarts) return 1;
          return 0;
        })
        .slice(0, 5);

      const [postsResult, servicesSnapshot] = await Promise.all([
        searchPosts(term, 5),
        getDocs(query(collection(db, 'services'), orderBy('createdAt', 'desc'), limit(20)))
      ]);
      const foundPosts = postsResult.success ? (postsResult.posts || []).slice(0, 5) : [];
      const foundServices = servicesSnapshot.docs
        .map((d) => normalizeService({ id: d.id, ...d.data() }))
        .filter((s) => {
          const caption = s.caption || '';
          const description = s.description || '';
          const category = s.categoryName || s.category || '';
          const combined = `${caption} ${description} ${category}`;
          return allWordsPresent(combined, term);
        })
        .slice(0, 5);

      if (inputValueRef.current !== term) return;
      setSuggestions({ users: foundUsers, posts: foundPosts, services: foundServices });
      setShowSuggestions(true);
    } catch (err) {
      if (inputValueRef.current !== term) return;
      console.error('Error al buscar sugerencias:', err);
    }
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      if (inputValue.trim()) fetchSuggestions(inputValue);
      else {
        setSuggestions({ users: [], posts: [], services: [] });
        setShowSuggestions(false);
      }
    }, 200);
    return () => clearTimeout(t);
  }, [inputValue, fetchSuggestions]);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (showSuggestions && searchContainerRef.current && !searchContainerRef.current.contains(e.target)) {
        setShowSuggestions(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [showSuggestions]);

  const handleSearchSubmit = (e) => {
    e?.preventDefault();
    const term = (inputValue || '').trim();
    if (term) navigate(`/explore?q=${encodeURIComponent(term)}`);
    else navigate('/explore');
  };

  // Actualizar URL en vivo mientras escribe
  useEffect(() => {
    const t = setTimeout(() => {
      const term = (inputValue || '').trim();
      if (term) navigate(`/explore?q=${encodeURIComponent(term)}`, { replace: true });
      else navigate('/explore', { replace: true });
    }, 300);
    return () => clearTimeout(t);
  }, [inputValue, navigate]);

  // Base posts (feed o búsqueda)
  const basePosts = useMemo(() => (q ? (searchedPosts || []) : (explorePosts || [])), [q, searchedPosts, explorePosts]);

  const blockedFiltered = useMemo(() => basePosts.filter((p) => !blockedIds.includes(p.userId)), [basePosts, blockedIds]);

  const categoryFiltered = useMemo(() => {
    if (selectedCategory === 'all') return blockedFiltered;
    return blockedFiltered.filter((p) => p?.category === selectedCategory);
  }, [blockedFiltered, selectedCategory]);

  // Si estamos buscando, el filtrado por texto adicional se hace por seguridad (searchPosts ya filtra, pero esto soporta futuros cambios)
  const finalPosts = useMemo(() => {
    if (!q) return categoryFiltered;
    return categoryFiltered.filter((p) => {
      const caption = p.caption || '';
      const userName = p.userName || '';
      const combined = `${caption} ${userName}`;
      return allWordsPresent(combined, q);
    });
  }, [categoryFiltered, q]);

  const isLoading = q ? searchLoading : exploreLoading;
  const error = q ? searchError : exploreError;

  const filteredUsers = useMemo(() => (users || []).filter((u) => !blockedIds.includes(u.id)), [users, blockedIds]);
  const filteredServices = useMemo(() => (services || []).filter((s) => !blockedIds.includes(s.userId)), [services, blockedIds]);

  return (
    <Layout>
      <div className="py-6">
        {/* Hero / Banner */}
        <Hero
          imageUrls={heroImageUrls}
          title="Explorar"
          subtitle="Descubre artistas, servicios y nuevas ideas"
          extraText="Explora por categorías o busca en vivo"
        />

        <div className="container-custom mt-8">
          {/* Barra de búsqueda con sugerencias */}
          <form onSubmit={handleSearchSubmit} className="mb-8" ref={searchContainerRef}>
            <div className="relative flex gap-3">
              <input
                type="text"
                placeholder="Buscar usuarios, posteos o servicios..."
                value={inputValue}
                onChange={(e) => setInputValue(e.target.value)}
                onFocus={() => {
                  if (inputValue.trim() && (suggestions.users.length + suggestions.posts.length + suggestions.services.length > 0)) {
                    setShowSuggestions(true);
                  }
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') handleSearchSubmit();
                  if (e.key === 'Escape') setShowSuggestions(false);
                }}
                className="flex-1 px-4 py-3 rounded-xl border border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-slate-400 focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              />
              <Button type="submit" variant="gradient">
                Buscar
              </Button>

              {showSuggestions && (suggestions.users.filter(u => !blockedIds.includes(u.id)).length + suggestions.posts.filter(p => !blockedIds.includes(p.userId || p.authorId)).length + suggestions.services.filter(s => !blockedIds.includes(s.userId)).length > 0) && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl shadow-xl max-h-96 overflow-y-auto z-50">
                  {suggestions.users.filter(u => !blockedIds.includes(u.id)).length > 0 && (
                    <div className="p-2">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2">Usuarios</p>
                      {suggestions.users.filter(u => !blockedIds.includes(u.id)).map(user => (
                        <Link
                          key={user.id}
                          to={`/profile/${user.id}`}
                          onClick={() => setShowSuggestions(false)}
                          className="flex items-center gap-3 px-3 py-2 hover:bg-gray-100 dark:hover:bg-slate-700 rounded-lg transition-colors"
                        >
                          <Avatar
                            src={user.photoURL || user.profileImage}
                            alt={user.username || user.displayName}
                            fallbackText={user.username || user.displayName}
                            size="sm"
                          />
                          <div className="flex-1 min-w-0">
                            <PremiumName
                              name={user.username || user.displayName || 'Usuario'}
                              isPremium={user.isPremium}
                              premiumColor={user.premiumNameColor}
                              nameClassName="text-sm font-medium text-gray-900 dark:text-white truncate block"
                            />
                            {user.profession && <p className="text-xs text-gray-500 dark:text-gray-400 truncate">{user.profession}</p>}
                          </div>
                        </Link>
                      ))}
                    </div>
                  )}
                  {suggestions.posts.filter(p => !blockedIds.includes(p.userId || p.authorId)).length > 0 && (
                    <div className="p-2 border-t border-gray-200 dark:border-slate-700">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2">Posteos</p>
                      {suggestions.posts.filter(p => !blockedIds.includes(p.userId || p.authorId)).map(post => (
                        <PostSuggestion key={post.id} post={post} onClick={() => { navigate(`/post/${post.id}`); setShowSuggestions(false); }} />
                      ))}
                    </div>
                  )}
                  {suggestions.services.filter(s => !blockedIds.includes(s.userId)).length > 0 && (
                    <div className="p-2 border-t border-gray-200 dark:border-slate-700">
                      <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 px-3 py-2">Servicios</p>
                      {suggestions.services.filter(s => !blockedIds.includes(s.userId)).map(service => (
                        <ServiceSuggestion key={service.id} service={service} onClick={() => { setSelectedService(service); setShowSuggestions(false); }} />
                      ))}
                    </div>
                  )}
                  <div className="p-2 border-t border-gray-200 dark:border-slate-700">
                    <button type="button" onClick={() => { handleSearchSubmit(); setShowSuggestions(false); }} className="w-full px-3 py-2 text-sm text-indigo-600 dark:text-indigo-400 hover:bg-indigo-50 dark:hover:bg-indigo-900/20 rounded-lg transition-colors font-medium">
                      Ver todos los resultados para &quot;{inputValue}&quot;
                    </button>
                  </div>
                </div>
              )}
            </div>
          </form>

          {/* Colecciones por categoría */}
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white">Colecciones</h2>
              {selectedCategory !== 'all' && (
                <button
                  type="button"
                  className="text-sm font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                  onClick={() => setSelectedCategory('all')}
                >
                  Ver todas
                </button>
              )}
            </div>

            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
              {CATEGORIES.filter((c) => c.id !== 'all').slice(0, 12).map((cat) => (
                <button
                  key={cat.id}
                  type="button"
                  onClick={() => setSelectedCategory(cat.id)}
                  className={`group text-left rounded-2xl border p-4 transition-all ${
                    selectedCategory === cat.id
                      ? 'border-indigo-500 bg-indigo-50 dark:bg-indigo-900/20 dark:border-indigo-500'
                      : 'border-gray-200 dark:border-slate-700 bg-white dark:bg-slate-800 hover:shadow-md'
                  }`}
                >
                  <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-indigo-600 to-purple-600 mb-3" />
                  <div className="text-sm font-semibold text-gray-900 dark:text-white">{cat.name}</div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 mt-1">Explorar</div>
                </button>
              ))}
            </div>
          </div>

          {/* Chips de categoría (rápidas) */}
          <div className="mb-6">
            <div className="flex gap-2 overflow-x-auto pb-2 hide-scrollbar">
              {CATEGORIES.map((category) => (
                <button
                  key={category.id}
                  onClick={() => setSelectedCategory(category.id)}
                  className={`px-4 py-2 rounded-full whitespace-nowrap font-medium text-sm transition-all ${
                    selectedCategory === category.id
                      ? 'bg-indigo-600 text-white shadow-md'
                      : 'bg-white dark:bg-slate-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-700 border border-gray-200 dark:border-slate-700'
                  }`}
                >
                  {category.name}
                </button>
              ))}
            </div>
          </div>

          {/* Estados */}
          {isLoading && finalPosts.length === 0 && <LoadingState message={q ? 'Buscando...' : 'Cargando posts...'} />}

          {error && (
            <ErrorState
              title={q ? 'No se pudo buscar' : 'No se pudo cargar'}
              message={error}
              onAction={q ? () => navigate(`/explore?q=${encodeURIComponent(q)}`) : refresh}
            />
          )}

          {!isLoading && !error && q && filteredUsers.length === 0 && filteredServices.length === 0 && finalPosts.length === 0 && (
            <EmptyState
              title="No encontramos resultados"
              description={
                'Probá con otra búsqueda o cambiá la categoría.'
              }
              actionLabel="Limpiar búsqueda"
              onAction={() => navigate('/explore')}
            />
          )}

          {/* Secciones de resultados cuando hay query */}
          {!isLoading && !error && q && (
            <>
              <section className="mb-10">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Usuarios ({filteredUsers.length})
                </h2>
                {filteredUsers.length > 0 ? (
                  <div className="flex gap-4 overflow-x-auto pb-4 snap-x snap-mandatory">
                    {filteredUsers.slice(0, 20).map((user) => (
                      <Link
                        key={user.id}
                        to={`/profile/${user.id}`}
                        className="flex-shrink-0 w-48 bg-white dark:bg-slate-800 border border-gray-200 dark:border-slate-700 rounded-xl p-4 hover:shadow-lg transition-all snap-start"
                      >
                        <div className="flex flex-col items-center text-center">
                          <Avatar
                            src={user.photoURL || user.profileImage}
                            alt={user.username || user.displayName}
                            fallbackText={user.username || user.displayName}
                            size="lg"
                            className="mb-3"
                          />
                          <PremiumName
                            name={user.username || user.displayName || 'Usuario'}
                            isPremium={user.isPremium}
                            premiumColor={user.premiumNameColor}
                            className="w-full justify-center"
                            nameClassName="font-semibold text-gray-900 dark:text-white truncate block w-full"
                          />
                          {user.profession && (
                            <p className="text-xs text-gray-500 dark:text-gray-400 truncate w-full">
                              {user.profession}
                            </p>
                          )}
                          {user.followersCount !== undefined && (
                            <p className="text-xs text-gray-400 dark:text-gray-500 mt-2">
                              {user.followersCount} seguidores
                            </p>
                          )}
                        </div>
                      </Link>
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 py-4">No hay usuarios para esta búsqueda.</p>
                )}
              </section>

              <section className="mb-10">
                <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">
                  Servicios ({filteredServices.length})
                </h2>
                {filteredServices.length > 0 ? (
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5 gap-6">
                    {filteredServices.slice(0, 20).map((service) => (
                      <ServiceCard
                        key={service.id}
                        service={service}
                        onClick={setSelectedService}
                        currentUserId={userData?.id}
                        savedServiceIds={userData?.savedServices || []}
                      />
                    ))}
                  </div>
                ) : (
                  <p className="text-gray-500 dark:text-gray-400 py-4">No hay servicios para esta búsqueda.</p>
                )}
              </section>
            </>
          )}

          {!isLoading && !error && finalPosts.length > 0 && (
            <>
              <MasonryGrid>
                {finalPosts.map((post) => (
                  <PostCard
                    key={post.id}
                    post={post}
                    currentUserId={userData?.id}
                    savedPostIds={userData?.savedPosts || []}
                  />
                ))}
              </MasonryGrid>

              {!q && hasMore && (
                <div className="text-center mt-8">
                  <Button variant="outline" onClick={loadMore} loading={exploreLoading}>
                    Cargar más
                  </Button>
                </div>
              )}
            </>
          )}
        </div>
      </div>

      {selectedService && (
        <ServiceModal
          service={selectedService}
          onClose={() => setSelectedService(null)}
          currentUserId={userData?.id}
          savedServiceIds={userData?.savedServices || []}
        />
      )}
    </Layout>
  );
};

export default ExplorePage;

